import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createDepartment,
  getDepartmentDetails,
  getDepartments,
  updateDepartment,
} from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import type { DepartmentDetails } from '../../types/department';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import {
  isConcurrencyConflict,
  organizationErrorMessage,
} from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<
  RootStackParamList,
  'DepartmentCreate'
>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'DepartmentEdit'>;
type Props = CreateProps | EditProps;

type FormState = {
  departmentCode: string;
  departmentName: string;
  description: string;
  locationId: number | null;
  locationLabel: string | null;
  parentDepartmentId: number | null;
  parentDepartmentLabel: string | null;
  isActive: boolean;
};

const EMPTY: FormState = {
  departmentCode: '',
  departmentName: '',
  description: '',
  locationId: null,
  locationLabel: null,
  parentDepartmentId: null,
  parentDepartmentLabel: null,
  isActive: true,
};

function fromDetails(item: DepartmentDetails): FormState {
  return {
    departmentCode: item.departmentCode,
    departmentName: item.departmentName,
    description: item.description ?? '',
    locationId: item.locationId,
    locationLabel: item.locationName,
    parentDepartmentId: item.parentDepartmentId,
    parentDepartmentLabel: item.parentDepartmentName,
    isActive: item.isActive !== false,
  };
}

export function validateDepartmentForm(
  form: FormState,
  isEdit: boolean,
): string | null {
  if (!isEdit && !form.departmentCode.trim()) {
    return 'Department code is required.';
  }
  if (!form.departmentName.trim()) return 'Department name is required.';
  return null;
}

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'DepartmentEdit';
}

export function DepartmentFormScreen(props: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
      switchLabel: { ...typography.body, color: palette.textPrimary },
    }),
    [palette],
  );

  const isEdit = isEditRoute(props);
  const departmentId = isEdit ? props.route.params.departmentId : null;
  const navigation = props.navigation;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [details, setDetails] = useState<DepartmentDetails | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    if (!departmentId) return;
    setLoading(true);
    try {
      const data = await getDepartmentDetails(departmentId);
      setDetails(data);
      setForm(fromDetails(data));
      dirty.current = false;
      setError(null);
    } catch (caught) {
      setError(
        organizationErrorMessage(
          caught,
          'The requested record could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    if (isEdit) load().catch(() => undefined);
  }, [isEdit, load]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => ({ ...current, [key]: value }));
  }

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  const loadParentOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getDepartments({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items
        .filter(item => !departmentId || item.departmentId !== departmentId)
        .map(item => ({
          id: item.departmentId,
          label: item.departmentName,
          subtitle: item.departmentCode,
        }));
    },
    [departmentId],
  );

  const loadLocationOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getLocations({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.locationId,
        label: item.locationName,
        subtitle: item.locationCode,
      }));
    },
    [],
  );

  async function submit() {
    if (savingLock.current) return;
    const validation = validateDepartmentForm(form, isEdit);
    if (validation) {
      setFieldError(validation);
      return;
    }
    if (
      isEdit &&
      departmentId &&
      form.parentDepartmentId === departmentId
    ) {
      setFieldError('A department cannot be its own parent.');
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);

    try {
      if (isEdit && departmentId && details) {
        await runOperation('update', () => updateDepartment(departmentId, {
          locationId: form.locationId,
          parentDepartmentId: form.parentDepartmentId,
          departmentName: form.departmentName,
          description: form.description,
          displayOrder: details.displayOrder ?? 0,
          isActive: form.isActive,
          rowVersion: details.rowVersion,
        }));
      } else {
        await runOperation('add', () => createDepartment({
          locationId: form.locationId,
          parentDepartmentId: form.parentDepartmentId,
          departmentCode: form.departmentCode,
          departmentName: form.departmentName,
          description: form.description,
        }));
      }
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught) && isEdit) {
        const message = organizationErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          organizationErrorMessage(
            caught,
            isEdit
              ? 'Unable to update department.'
              : 'Unable to create department.',
          ),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  const permission = isEdit
    ? PERMISSIONS.DEPARTMENT_UPDATE
    : PERMISSIONS.DEPARTMENT_CREATE;

  return (
    <PermissionGuard permission={permission} fallback={<UnauthorizedScreen />}>
      {loading ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title={isEdit ? 'Edit department' : 'Create department'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading department" />
        </AppScreen>
      ) : error && isEdit && !details ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit department"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title={isEdit ? 'Edit department' : 'Create department'}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {isEdit ? (
              <>
                <Text style={themed.readOnlyLabel}>Department code</Text>
                <Text style={themed.readOnlyValue}>{form.departmentCode}</Text>
              </>
            ) : (
              <AppInput
                label="Department code *"
                value={form.departmentCode}
                onChangeText={value => setField('departmentCode', value)}
                autoCapitalize="characters"
              />
            )}
            <AppInput
              label="Department name *"
              value={form.departmentName}
              onChangeText={value => setField('departmentName', value)}
            />
            <AppInput
              label="Description"
              value={form.description}
              onChangeText={value => setField('description', value)}
            />
            <SearchableEntitySelector
              label="Location"
              selectedValue={form.locationId}
              selectedLabel={form.locationLabel}
              loadOptions={loadLocationOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: option.id,
                  locationLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: null,
                  locationLabel: null,
                }));
              }}
              placeholder="Search locations"
            />
            <SearchableEntitySelector
              label="Parent department"
              selectedValue={form.parentDepartmentId}
              selectedLabel={form.parentDepartmentLabel}
              loadOptions={loadParentOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  parentDepartmentId: option.id,
                  parentDepartmentLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  parentDepartmentId: null,
                  parentDepartmentLabel: null,
                }));
              }}
              placeholder="Search departments"
            />
            {isEdit ? (
              <View style={styles.switchRow}>
                <Text style={themed.switchLabel}>Active</Text>
                <Switch
                  value={form.isActive}
                  onValueChange={value => setField('isActive', value)}
                />
              </View>
            ) : null}
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton title="Save" onPress={submit} loading={saving} />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={confirmLeave}
            />
          </AppCard>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
